import { LocationOn, OpenInNew, Schedule, Map, Notes, Tag, AccessTime } from '@mui/icons-material';
import { CircularProgress, Box } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { LocationContactsList } from './LocationContactsList';
import { MobileLocationSectionProps } from './MobileLocationSection.props';
import {
  LocationContainer,
  AddressRow,
  AddressContent,
  AddressLabel,
  AddressText,
  MapsButton,
  SectionDivider,
  ZoneBlock,
  ZoneText,
  TradingHoursBlock,
  TradingHoursRow,
  TradingDay,
  TradingTime,
  MapEmbedContainer,
  NoDataText,
  NotesBlock,
  NotesText,
  ReferenceRow,
  ReferenceLabel,
  ReferenceValue,
} from './MobileLocationSection.styles';
import {
  buildGoogleMapsUrl,
  buildEmbedUrl,
  buildAllDayHours,
  buildCoverageHours,
} from './MobileLocationSection.utils';
import { COLORS } from '../../../../../configs/colors';

interface NotesRefsProps {
  icon: React.ReactNode;
  label: string;
  notes?: string;
  refs: { ref1?: string; ref2?: string; ref3?: string };
}

const NotesReferencesBlock: React.FC<NotesRefsProps> = ({ icon, label, notes, refs }) => {
  const hasRefs = refs.ref1 || refs.ref2 || refs.ref3;
  return (
    <NotesBlock>
      <AddressLabel>
        {icon}
        {label}
      </AddressLabel>
      {notes && <NotesText>{notes}</NotesText>}
      {hasRefs && (
        <Box sx={{ mt: notes ? 1 : 0 }}>
          {refs.ref1 && (
            <ReferenceRow>
              <ReferenceLabel>Reference 1</ReferenceLabel>
              <ReferenceValue>{refs.ref1}</ReferenceValue>
            </ReferenceRow>
          )}
          {refs.ref2 && (
            <ReferenceRow>
              <ReferenceLabel>Reference 2</ReferenceLabel>
              <ReferenceValue>{refs.ref2}</ReferenceValue>
            </ReferenceRow>
          )}
          {refs.ref3 && (
            <ReferenceRow>
              <ReferenceLabel>Reference 3</ReferenceLabel>
              <ReferenceValue>{refs.ref3}</ReferenceValue>
            </ReferenceRow>
          )}
        </Box>
      )}
    </NotesBlock>
  );
};

export const MobileLocationSection: React.FC<MobileLocationSectionProps> = ({
  service,
  locationAttributes,
  tradingHours,
  locationReferenceNumbers,
  locationContacts,
  coverageHours,
  isLoading,
}) => {
  const [mapError, setMapError] = useState(false);

  const serviceAddress = locationAttributes?.serviceAddress?.value || service.serviceAddress;
  const nodeAddress = locationAttributes?.selectedNodeAddress?.value;
  const displayAddress = serviceAddress || nodeAddress;

  const mapsUrl = buildGoogleMapsUrl(displayAddress);
  const embedUrl = buildEmbedUrl(displayAddress);

  useEffect(() => {
    setMapError(false);
  }, [embedUrl]);
  const allDayHours = useMemo(() => buildAllDayHours(tradingHours), [tradingHours]);

  const zone = service.zone || service.mainLocationZone;

  const locationNotes = service.mainLocationNotes;
  const locationRefValue = locationReferenceNumbers?.[0]?.value;
  const locationRef1 = locationRefValue?.referenceNo1 as string | undefined;
  const locationRef2 = locationRefValue?.referenceNo2 as string | undefined;
  const locationRef3 = locationRefValue?.referenceNo3 as string | undefined;

  const subLocationNotes = service.subLocationNotes;
  const subLocationRefs = service.subLocationReferences;

  const allCoverageHours = useMemo(() => buildCoverageHours(coverageHours), [coverageHours]);

  const today = useMemo(() => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ padding: '16px', backgroundColor: COLORS.White, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <LocationContainer>
      {/* 1. Address with Google Maps button */}
      <AddressRow>
        <AddressContent>
          <AddressLabel>
            <LocationOn sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
            Service Address
          </AddressLabel>
          {displayAddress ? (
            <>
              {displayAddress.placeName && (
                <AddressText sx={{ fontWeight: 600 }}>{displayAddress.placeName}</AddressText>
              )}
              <AddressText>{displayAddress.line1}</AddressText>
              {displayAddress.line2 && <AddressText>{displayAddress.line2}</AddressText>}
              <AddressText>
                {[
                  displayAddress.suburb,
                  displayAddress.stateName || displayAddress.state,
                  displayAddress.postCode,
                ]
                  .filter(Boolean)
                  .join(' ')}
              </AddressText>
            </>
          ) : (
            <NoDataText>No address available</NoDataText>
          )}
        </AddressContent>
        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open in Google Maps"
          >
            <MapsButton>
              <OpenInNew />
            </MapsButton>
          </a>
        )}
      </AddressRow>

      {/* 2. Zone info */}
      {zone && (
        <>
          <SectionDivider />
          <ZoneBlock>
            <AddressLabel>
              <Map sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
              Zone
            </AddressLabel>
            <ZoneText>{zone}</ZoneText>
          </ZoneBlock>
        </>
      )}

      {/* 3. Contacts */}
      {locationContacts && locationContacts.length > 0 && (
        <>
          <SectionDivider />
          <LocationContactsList contacts={locationContacts} />
        </>
      )}

      {/* 4. Location Notes & References */}
      {(locationNotes || locationRef1 || locationRef2 || locationRef3) && (
        <>
          <SectionDivider />
          <NotesReferencesBlock
            icon={<Notes sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />}
            label="Location Notes & References"
            notes={locationNotes}
            refs={{ ref1: locationRef1, ref2: locationRef2, ref3: locationRef3 }}
          />
        </>
      )}

      {/* 5. Sublocation Notes & References */}
      {(subLocationNotes ||
        subLocationRefs?.referenceNo1 ||
        subLocationRefs?.referenceNo2 ||
        subLocationRefs?.referenceNo3) && (
        <>
          <SectionDivider />
          <NotesReferencesBlock
            icon={<Tag sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />}
            label="Sublocation Notes & References"
            notes={subLocationNotes}
            refs={{
              ref1: subLocationRefs?.referenceNo1,
              ref2: subLocationRefs?.referenceNo2,
              ref3: subLocationRefs?.referenceNo3,
            }}
          />
        </>
      )}

      {/* 6. Trading Hours - all 7 days */}
      <SectionDivider />
      <TradingHoursBlock>
        <AddressLabel>
          <Schedule sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
          Trading Hours
        </AddressLabel>
        {allDayHours.map((dh) => (
          <TradingHoursRow key={dh.day} isToday={dh.day === today}>
            <TradingDay>{dh.day}</TradingDay>
            <TradingTime noHours={!dh.hasHours}>
              {dh.hasHours ? `${dh.start} - ${dh.end}` : 'No Trading Hours'}
            </TradingTime>
          </TradingHoursRow>
        ))}
      </TradingHoursBlock>

      {/* 7. Coverage Hours */}
      {allCoverageHours.some((ch) => ch.hasHours) && (
        <>
          <SectionDivider />
          <TradingHoursBlock>
            <AddressLabel>
              <AccessTime sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
              Coverage Hours
            </AddressLabel>
            {allCoverageHours.map((ch) => (
              <TradingHoursRow key={ch.day} isToday={ch.day === today}>
                <TradingDay>{ch.day}</TradingDay>
                <TradingTime noHours={!ch.hasHours}>
                  {ch.hasHours ? `${ch.start} - ${ch.end}` : 'No Coverage'}
                </TradingTime>
              </TradingHoursRow>
            ))}
          </TradingHoursBlock>
        </>
      )}

      {/* 8. Embedded Google Map */}
      {embedUrl && (
        <>
          <SectionDivider />
          <MapEmbedContainer>
            {mapError ? (
              <NoDataText>Map unavailable — use &ldquo;Open in Google Maps&rdquo; above</NoDataText>
            ) : (
              <iframe
                src={embedUrl}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                title="Location Map"
                onError={() => setMapError(true)}
              />
            )}
          </MapEmbedContainer>
        </>
      )}
    </LocationContainer>
  );
};
